import {
  logWriteErrorDiagnostics,
  logWriteRequestDiagnostics,
  logWriteResponseDiagnostics,
} from 'src/auth/auth-diagnostics';

type FetchOptionsLike = {
  method?: string;
  headers?: HeadersInit;
  body?: unknown;
};

type LoadFailedError = {
  name?: string;
  message?: string;
};

export function logApiClientFetchStart(url: string, method: string, fetchOptions: FetchOptionsLike): void {
  // Per-call console logging is dev-only: failures are reported via the
  // monitoring facade (RUM), so production consoles stay quiet.
  if (import.meta.env.DEV) {
    console.debug('[apiClientFetchQueue] Starting fetch:', {
      url,
      method,
      hasHeaders: !!fetchOptions?.headers,
      hasBody: !!fetchOptions?.body,
      timestamp: Date.now(),
    });
  }
  logWriteRequestDiagnostics(url, method, fetchOptions?.headers);
}

export function logApiClientFetchSuccess(
  url: string,
  method: string,
  status: number,
  statusText: string,
  fetchOptions: FetchOptionsLike,
): void {
  if (import.meta.env.DEV) {
    console.debug('[apiClientFetchQueue] Fetch successful:', {
      url,
      status,
      statusText,
      timestamp: Date.now(),
    });
  }
  logWriteResponseDiagnostics(url, method, status, fetchOptions?.headers);
}

export function logApiClientFetchFailure(
  url: string,
  method: string,
  error: unknown,
  fetchOptions: FetchOptionsLike,
): void {
  const e = error as any;
  console.error('[apiClientFetchQueue] Fetch failed - Detailed error:', {
    url,
    error: e,
    message: e?.message,
    name: e?.name,
    stack: e?.stack,
    originalError: e?.originalError,
    cause: e?.cause,
    timestamp: Date.now(),
    fetchOptions: {
      method: fetchOptions?.method,
      headers: fetchOptions?.headers,
    },
  });
  logWriteErrorDiagnostics(url, method, e?.status ?? e?.response?.status ?? undefined, fetchOptions?.headers);
}

export function logApiClientLoadFailedContext(url: string, error: unknown): void {
  const e = error as LoadFailedError;
  if (e?.name === 'TypeError' && e?.message?.includes('Load failed')) {
    console.error('[apiClientFetchQueue] TypeError: Load failed detected - possible causes:', {
      possibleCauses: [
        'CORS issue - check if the API allows requests from this origin',
        'Network connectivity issue',
        'SSL/TLS certificate issue',
        'Content Security Policy blocking the request',
        'Ad blocker or browser extension interference',
      ],
      url,
      origin: window.location.origin,
    });
  }
}
